import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Grid,
  Link as LinkMui,
  TableCell,
  TableRow,
  TableHead,
  Table,
  Typography,
  TableBody,
  Container,
  Skeleton,
} from '@mui/material'
import { Link as LinkRouter, useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, ExpandMore } from '@mui/icons-material'
import { invoiceType, invoiceLineType, salesOrderType, receivePaymentType, customerCreditType } from '../../types'
import { useContext, useEffect, useState } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'
import './Invoice.css'
import ErrorScreen from '../../Misc/ErrorScreen'
import { dataContext } from '../../'
import { getRepCodeName } from '../../helper'

function Invoice() {
  type invoiceLine = invoiceLineType & {
    item_name: string
  }
  type invoiceDetail = invoiceType & {
    customer_name: string
    rep_code: string | null
    parent_customer_name: string | null
  }
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, salesReps } = useContext(dataContext)

  const [invoice, setInvoice] = useState<invoiceDetail>({} as invoiceDetail)
  const [invoiceLines, setInvoiceLines] = useState<invoiceLine[]>([])
  const [salesOrder, setSalesOrder] = useState<salesOrderType[]>([])
  const [payments, setPayments] = useState<receivePaymentType[]>([])
  const [customerCredit, setCustomerCredit] = useState<customerCreditType[]>([])
  const [stickyHeader, setStickyHeader] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<'ok' | 'error' | 'unauthorized'>('ok')
  useEffect(() => {
    if (id) {
      setLoading(true)
      setLoadError('ok')
      if (!user.active_flag) return

      axios
        .get(`/invoice/${id}?${user.user_type === 'Sales Rep' ? `salesUser=${user.user_id}` : ''}`)
        .then(res => {
          setInvoice(res.data.invoice)
          setInvoiceLines(res.data.invoiceLines)
          setSalesOrder(res.data.salesOrder)
          setPayments(res.data.payments)
          setCustomerCredit(res.data.customerCredit)
          setLoadError('ok')
        })
        .catch(err => {
          console.error(err)
          if (err.response?.status === 401) {
            setLoadError('unauthorized')
          } else {
            setLoadError('error')
          }
        })
        .finally(() => setLoading(false))
    }
  }, [user])

  useEffect(() => {
    document.title = `Invoice ${invoice.ref_number ? `| ${invoice.ref_number}` : ''} | Salestrack`
  }, [invoice])

  return (
    <div className="invoice-detail">
      <div className="invoice-detail-button-container">
        <Button
          size="large"
          startIcon={<ChevronLeft />}
          variant="contained"
          className="invoice-detail-button"
          component={LinkRouter}
          to="/customer/invoices"
          loading={loading}
          onClick={e => {
            e.preventDefault()
            navigate(-1)
          }}
        >
          Return
        </Button>
        {loading ? (
          <Skeleton width={220} height={32} />
        ) : (
          <Chip label={<Typography variant="h6">Invoice #: {invoice.ref_number}</Typography>} />
        )}
      </div>
      {loadError !== 'ok' ? (
        <div className="invoice-detail-error-container">
          <ErrorScreen
            errorText={
              loadError === 'unauthorized'
                ? 'You are not authorized to view this invoice.'
                : 'Failed to load invoice details. Please try again or contact support if the error persists.'
            }
            title="Invoice Load Failed"
          />
        </div>
      ) : (
        <>
          <div className="invoice-detail-data-container">
            <div className="invoice-detail-data-container-header">
              <Container className="invoice-detail-header-container" maxWidth={false}>
                <Container className="invoice-detail-header-column" maxWidth={false}>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={100} /> : 'Customer Name:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? <Skeleton width={120} /> : invoice.parent_customer_name ?? '-'}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={80} /> : 'Job Name:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? <Skeleton width={100} /> : invoice.customer_name}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={100} /> : 'Total Amount:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? (
                        <Skeleton width={100} />
                      ) : (
                        ((invoice.balance_amount ?? 0) - (invoice.applied_amount ?? 0)).toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })
                      )}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={110} /> : 'Applied Amount:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? (
                        <Skeleton width={100} />
                      ) : (
                        invoice.applied_amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                      )}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={110} /> : 'Balance Amount:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? (
                        <Skeleton width={100} />
                      ) : (
                        (invoice.balance_amount ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                      )}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={120} /> : 'Total Sales Tax:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? (
                        <Skeleton width={100} />
                      ) : (
                        (invoice.total_sales_tax ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                      )}
                    </Typography>
                  </Grid>
                </Container>
                <Container className="invoice-detail-header-column" maxWidth={false}>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={120} /> : 'Transaction Date:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? (
                        <Skeleton width={100} />
                      ) : invoice.txn_date ? (
                        dayjs(invoice.txn_date).format('MM/DD/YYYY')
                      ) : (
                        '-'
                      )}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={80} /> : 'Due Date:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? (
                        <Skeleton width={100} />
                      ) : invoice.due_date ? (
                        dayjs(invoice.due_date).format('MM/DD/YYYY')
                      ) : (
                        '-'
                      )}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={100} /> : 'Customer PO:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? <Skeleton width={120} /> : invoice.customer_po_number}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={90} /> : 'Sales Rep:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? (
                        <Skeleton width={100} />
                      ) : (
                        getRepCodeName(salesReps.find(rep => rep.initial === invoice.rep_code))
                      )}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={60} /> : 'DPAS:'}
                    </Typography>
                    <Typography variant="body1">{loading ? <Skeleton width={80} /> : invoice.dpass ?? '-'}</Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={150} /> : 'QB Last Update Date:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? (
                        <Skeleton width={100} />
                      ) : invoice.last_update_date_qb ? (
                        dayjs(invoice.last_update_date_qb).format('MM/DD/YYYY')
                      ) : (
                        '-'
                      )}
                    </Typography>
                  </Grid>
                </Container>
                <Container className="invoice-detail-header-column" maxWidth={false}>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={110} /> : 'Bill Address Attention:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? <Skeleton width={150} /> : invoice.bill_address_attention ?? '-'}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={110} /> : 'Bill Address Addressee:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? <Skeleton width={150} /> : invoice.bill_address_addressee ?? '-'}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={110} /> : 'Bill Address 1:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? <Skeleton width={150} /> : invoice.bill_address_addr1 ?? '-'}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={110} /> : 'Bill Address 2:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? <Skeleton width={150} /> : invoice.bill_address_addr2 ?? '-'}
                    </Typography>
                  </Grid>
                  <Grid className="invoice-detail-header-grid">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? <Skeleton width={120} /> : 'Bill Address City, State, Zip:'}
                    </Typography>
                    <Typography variant="body1">
                      {loading ? (
                        <Skeleton width={100} />
                      ) : (
                        `${invoice.bill_address_city}, ${invoice.bill_address_state} ${invoice.bill_address_zip}`
                      )}
                    </Typography>
                  </Grid>
                </Container>
              </Container>
            </div>
            <div className="invoice-detail-data-container-line-items">
              <Accordion
                slotProps={{
                  transition: {
                    onEntered: () => {
                      setStickyHeader(true)
                    },
                    onExit: () => {
                      setStickyHeader(false)
                    },
                  },
                }}
                defaultExpanded
              >
                <AccordionSummary
                  expandIcon={<ExpandMore className="MuiAccordionSummary-expandIcon" />}
                  className="invoice-accordion-summary"
                >
                  {loading ? (
                    <Skeleton width={220} height={32} />
                  ) : (
                    <Typography variant="h6">Invoice Line Items</Typography>
                  )}
                </AccordionSummary>
                <AccordionDetails className="invoice-accordion-details">
                  <Table color="primary" stickyHeader={stickyHeader}>
                    <TableHead>
                      {loading ? (
                        <TableRow>
                          {Array.from({ length: 6 }).map((_, idx) => (
                            <TableCell key={idx}>
                              <Skeleton className="skeleton-header" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell>SI. No.</TableCell>
                          <TableCell>Item Name</TableCell>
                          <TableCell width="40%">Description</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Rate</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      )}
                    </TableHead>
                    <TableBody>
                      {loading
                        ? Array.from({ length: 5 }).map((_, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Skeleton width={40} />
                              </TableCell>
                              <TableCell>
                                <Skeleton width={120} />
                              </TableCell>
                              <TableCell>
                                <Skeleton width={200} />
                              </TableCell>
                              <TableCell align="right">
                                <Skeleton width={60} />
                              </TableCell>
                              <TableCell align="right">
                                <Skeleton width={80} />
                              </TableCell>
                              <TableCell align="right">
                                <Skeleton width={100} />
                              </TableCell>
                            </TableRow>
                          ))
                        : invoiceLines.map(line => (
                            <TableRow key={line.invoice_line_txn_id}>
                              <TableCell>{line.line_numerical_order}</TableCell>
                              <TableCell>{line.item_name ? line.item_name : '-'}</TableCell>
                              <TableCell>{line.item_desc ? line.item_desc : '-'}</TableCell>
                              <TableCell align="right">{line.quantity ? line.quantity : '-'}</TableCell>
                              <TableCell align="right">
                                {line.rate
                                  ? line.rate.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                                  : '$0.00'}
                              </TableCell>
                              <TableCell align="right">
                                {line.amount
                                  ? line.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                                  : '$0.00'}
                              </TableCell>
                            </TableRow>
                          ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            </div>
            <div className="invoice-detail-data-container-related-items">
              {loading ? <Skeleton width={180} height={32} /> : <Typography variant="h6">Related Items</Typography>}
              <Table color="primary">
                <TableHead>
                  {loading ? (
                    <TableRow>
                      {Array.from({ length: 6 }).map((_, idx) => (
                        <TableCell key={idx}>
                          <Skeleton className="skeleton-header" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Reference Number</TableCell>
                      <TableCell align="right">Txn Date</TableCell>
                      <TableCell align="right">Due Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Skeleton width={120} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={80} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <>
                      {salesOrder.map(so => (
                        <TableRow key={so.sales_order_txn_id}>
                          <TableCell>Sales Order</TableCell>
                          <TableCell align="right">
                            <LinkMui to={`/salesOrder/${so.sales_order_txn_id}`} component={LinkRouter}>
                              {so.ref_number}
                            </LinkMui>
                          </TableCell>
                          <TableCell align="right">
                            {so.txn_date ? dayjs(so.txn_date).format('MM/DD/YYYY') : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {so.due_date ? dayjs(so.due_date).format('MM/DD/YYYY') : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {so.total_amount
                              ? so.total_amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                              : '-'}
                          </TableCell>
                          <TableCell>{so.is_fully_invoiced ? 'Invoiced' : 'Not Invoiced'}</TableCell>
                        </TableRow>
                      ))}
                      {customerCredit.map(cc => (
                        <TableRow key={cc.customer_credit_txn_id}>
                          <TableCell>Customer Credit</TableCell>
                          <TableCell align="right">
                            <LinkMui to={`/customer/credit/${cc.customer_credit_txn_id}`} component={LinkRouter}>
                              {cc.ref_number}
                            </LinkMui>
                          </TableCell>
                          <TableCell align="right">
                            {cc.txn_date ? dayjs(cc.txn_date).format('MM/DD/YYYY') : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {cc.due_date ? dayjs(cc.due_date).format('MM/DD/YYYY') : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {cc.total_amount
                              ? cc.total_amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                              : '-'}
                          </TableCell>
                          <TableCell>{cc.is_paid ? 'Paid' : 'Not Paid'}</TableCell>
                        </TableRow>
                      ))}
                      {payments.map(p => (
                        <TableRow key={p.receive_payment_txn_id}>
                          <TableCell>Payment</TableCell>
                          <TableCell align="right">
                            <LinkMui to={`/customer/payment/${p.receive_payment_txn_id}`} component={LinkRouter}>
                              {p.ref_number}
                            </LinkMui>
                          </TableCell>
                          <TableCell align="right">
                            {p.txn_date ? dayjs(p.txn_date).format('MM/DD/YYYY') : '-'}
                          </TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right">
                            {p.total_amount
                              ? p.total_amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                              : '-'}
                          </TableCell>
                          <TableCell>-</TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Invoice
